import { watch, type App } from "vue";
import { useRouter } from '@library/Services';

export function useBreadcrumbs()
{
    return Breadcrumbs.instance!;
}

export function useBreadcrumbsInstaller()
{
    return {
        install: async ( app: App<any> ) =>
        {
            Breadcrumbs.instance = new Breadcrumbs();
        }
    };
}

class Breadcrumbs
{

    public static instance?: Breadcrumbs;

    private ROOT_PATHS = [
        '/my-garage',
        '/my-marketplace',
        '/safety'
    ];

    private history: string[] = [];
    private isBackAction: boolean = false;
    private isFirstLoad: boolean = true;
    
    /**
     * Initialises the breadcrumbs
     */
    constructor()
    {
        const router = useRouter();
        watch( () => router.current.value.fullPath, fullPath => this.onRouteChanged( fullPath ) );
    }

    /**
     * Occurs when the route changes
     * @param fullPath 
     */
    private onRouteChanged( fullPath: string )
    {

        // If its a back action, dont add the new path onto the history or it will duplicate it
        if( this.isBackAction )
        {
            this.isBackAction = false;
            return;
        }

        const path = fullPath.split( '?' )[ 0 ];

        // If this is the first load, handle it
        if( this.isFirstLoad ) this.handleFirstLoad( path );

        // If the path is in the root paths, wipe the history, we've backed up to the top
        if( this.ROOT_PATHS.indexOf( path ) >= 0 )
        {
            this.history = [];    
        }
        
        // We need to check against the path without the query string parameters. They can get replaced at any stage.
        var currentPathIndex = this.history.map( s => s.split( '?' )[0] ).indexOf( path );

        // Add the path to the history, only if its isnt already in it
        if( this.history.length == 0 || currentPathIndex < 0 )
        {
            this.history.push( fullPath );
        }

        else if ( currentPathIndex > -1 )
        {
            /*
             * When using route navigation, the first path update will contain the path only and not the query string parameters.
             * However while the page is loading that path will get updated to include query string parameters such as consumer id,
             * vehicle id, etc. When this occurs, we need to replace the existing path so that we don't end up on pages that are 
             * missing details in the
             */
            this.history[currentPathIndex] = fullPath;

            // If the current index is not the last one in the history, then remove the rest of the history. This helps prevent finite loops.
            if ( currentPathIndex < this.history.length - 1 )
            {
                this.history = this.history.slice(0, currentPathIndex);
            }
        }

        localStorage.setItem( 'breadcrumbs', JSON.stringify( this.history ) );
        
    }

    /**
     * On a page refresh we need to check if we're in a child page and if so, insert the root page 
     * for the section the user is in.  In this instance, there will be one back step towards the root.
     * @param path 
     * @returns 
     */
    private handleFirstLoad( path: string )
    {

        const cachedBreadcrumbs = localStorage.getItem( 'breadcrumbs' ) ?? '[]';
        this.history = JSON.parse( cachedBreadcrumbs );
        this.isFirstLoad = false;

        if( this.history.length == 0 )
        {
            const root = this.ROOT_PATHS.find( p => path.indexOf( p ) == 0 && path != p );
            if( root == undefined ) return;
            this.history.push( root );
        }

    }

    /**
     * Performs a back action through the breadcrumbs
     */
    back()
    {

        // If the user is pressing back but there's no history, then there was a problem in the
        // the history, so clear it and send the back to my garage
        if( this.history.length == 0 )
        {
            this.history = [];
            const router = useRouter();
            router.go( '/my-garage' );
            return;
        } 

        // Take the last item off the history ( the current route )
        this.history.pop();

        // Go back
        this.isBackAction = true;

        const router = useRouter();
        router.go( this.history[ this.history.length - 1 ] );

    }
}